/**
 * Single source of truth for the organization's facts.
 *
 * Most of this was carried over from the previous Squarespace site
 * (artonthelakefestival.com) - see assets/old-website/ for the captured copy.
 *
 * The site is deliberately year-free: the festival has run in several years and
 * the record of which is incomplete, so naming years invited the inconsistency
 * it was meant to fix. Nothing here should carry a date. The full festival
 * history is kept in memory.md instead.
 */

export const site = {
  name: 'Art on the Lake',
  location: 'Pleasant Hill, IA',
  /**
   * The public address. www is canonical, not the bare domain: Hover cannot
   * point an apex at CloudFront, and its apex forward is HTTP-only, so
   * https://artonthelake.org has nothing listening. artonthelakefestival.com
   * redirects here.
   */
  domain: 'artonthelake.org',
  url: 'https://www.artonthelake.org',
  // Still on the old domain: the mailbox is Google Workspace on
  // artonthelakefestival.com and moving it is a separate job.
  email: 'info@artonthelakefestival.com',
  phone: '515.957.1180',
  phoneHref: 'tel:+15159571180',
  facebook: 'https://www.facebook.com/ArtontheLakeFestival/',
  tagline:
    'Art on the Lake promotes the arts in the community and supports local artists. Our goal is to give artists opportunities to flourish and increase awareness of the arts.',
};

export const venue = {
  name: 'Copper Creek Lake Park',
  address: '4390 E University Ave, Pleasant Hill, IA 50327',
  mapsUrl:
    'https://www.google.com/maps/search/?api=1&query=Copper+Creek+Lake+Park%2C+4390+E+University+Ave%2C+Pleasant+Hill%2C+IA+50327',
};

/**
 * Forward-looking copy for the period between festivals. Deliberately does not
 * announce that a given year has no festival - the site is a placeholder for
 * whatever comes next, not a cancellation notice.
 */
export const nextFestival = {
  heading: 'The next festival',
  body:
    'Details for the next Art on the Lake Festival will be posted here as soon as they are set.',
};

/**
 * What a festival day is like. Written as the things that are true every time
 * it runs, so the page reads the same whichever year you land on it.
 */
export interface FestivalFeature {
  title: string;
  body: string;
}

export const festivalFeatures: FestivalFeature[] = [
  {
    title: 'Free to the community',
    body: 'No admission charge, no ticket. Everyone is welcome for the whole day.',
  },
  {
    title: 'Artists on the shore',
    body: 'More than fifty artists show and sell their work along the trail at the water’s edge.',
  },
  {
    title: 'Live music',
    body: 'Local musicians play through the afternoon from the stage by the lake.',
  },
  {
    title: 'Food from local vendors',
    body: 'Lunch, snacks, and something cold to drink, all from vendors nearby.',
  },
  {
    title: 'Lil’ Artist Alley',
    body: 'Hands-on crafts for kids: spin art, painting, chalk drawing, and more.',
  },
  {
    title: 'Art on the trail',
    body: 'Chalk artists turn the paved loop around the lake into the biggest piece of the day.',
  },
];

export interface Sponsor {
  name: string;
  url?: string;
  logo?: string;
}

export interface SponsorTier {
  tier: string;
  blurb?: string;
  sponsors: Sponsor[];
}

/**
 * Sponsors as listed on the old festival sponsors page. The "Silver" tier was
 * present there but had no entries listed, so it is omitted here.
 */
export const sponsorTiers: SponsorTier[] = [
  {
    tier: 'Lasting Impression',
    blurb:
      'Bravo Greater Des Moines and BRAVO’s 17 local partner governments: Altoona, Ankeny, Bondurant, Carlisle, Clive, Des Moines, Grimes, Indianola, Johnston, Norwalk, Pleasant Hill, Polk City, Polk County, Urbandale, Waukee, West Des Moines, and Windsor Heights.',
    sponsors: [
      {
        name: 'Bravo Greater Des Moines',
        url: 'https://bravogreaterdesmoines.org/',
        logo: '/assets/sponsors/bravo-logo-digital-digital-rgb.jpg',
      },
    ],
  },
  {
    tier: 'Thumb Print',
    sponsors: [
      { name: 'Berean', url: 'https://bereanhub.com/', logo: '/assets/sponsors/logo-4.jpg' },
      {
        name: 'City of Pleasant Hill',
        url: 'https://www.pleasanthilliowa.org/',
        logo: '/assets/sponsors/pleasanthill-logo.jpg',
      },
    ],
  },
  {
    tier: 'Gold',
    sponsors: [
      {
        name: 'Copper Shores Village',
        url: 'https://immanuel.com/communities/copper-shores-village?hsLang=en',
        logo: '/assets/sponsors/copper-shores-village-logo-4c.jpg',
      },
    ],
  },
];

/**
 * The About page's story, supplied by the organization. It deliberately does
 * not repeat the "What we do" list below it.
 */
export const about = {
  lede:
    'Art on the Lake is a 501(c)(3) nonprofit organization dedicated to strengthening the community through the arts. Founded in Pleasant Hill, Iowa, the organization was created with the belief that art has the power to inspire creativity, bring people together, and enrich the lives of people of all ages. What began as a community arts festival has grown into an organization that supports local artists, celebrates creativity, and creates meaningful opportunities for people to experience and engage with the arts.',
  body:
    'Today, Art on the Lake continues to foster connections between artists and the community by making the arts accessible, welcoming, and inclusive. Through educational programming, community events, and partnerships, the organization strives to cultivate creativity, support local talent, and inspire the next generation of artists while enriching the cultural life of the Greater Des Moines area.',
};

/**
 * The community mural photo. It carries its own section on the home page, so
 * the photo strips leave it out rather than showing it twice.
 */
export const featurePhoto = 'paint-by-number-recovered.jpg';

/** What the organization does, as described on the old About page. */
export const whatWeDo: string[] = [
  'Educational workshops and classes for local artists.',
  'Opportunities to promote and sell art at little or no cost to the artist.',
  'Art and culture educational opportunities for all ages, open to the whole community.',
  'Support for other art and cultural opportunities in the area.',
];
